<template>
  <!-- Toast component -->
  <Toast>
    <template v-slot:wishlist:sign-in>
      <p class="wishlist_sign-in-toast-wrapper">
        <button class="btn wishlist_sign-in-cta" @click="handleSignIn">Sign In</button>
        <span class="wishlist_sign-in-help-text">to save wishlists to your account</span>
      </p>
    </template>

    <template v-slot:wishlist-page:sign-in>
      <p class="wishlist_sign-in-toast-wrapper">
        <button class="btn wishlist_sign-in-cta" @click="handleSignIn">Sign in</button>
        <span class="wishlist_sign-in-help-text">to share your wishlists</span>
      </p>
    </template>
  </Toast>

  <!-- Modal component -->
  <WishlistModal
    v-if="isSelectWishlistModalOpen"
    :showToast="showToast"
    :isDesktopScreen="isDesktopScreen"
  />
</template>

<script setup>
  import { onMounted, computed, watch } from 'vue';
  import { useStore } from 'vuex';
  import Toast from '../global/toast.vue';
  import WishlistModal from './components/wishlist/wishlist-modal.vue';
  import { deleteProductFromMultipleList } from './api';
  import { MEDIA_TYPES } from '../../utils/constants';
  import { useMediaType } from '../../utils/use-media-type';

  const store = useStore();
  const mediaType = useMediaType();

  const wishlistContentDetailsList = computed(() => store.state.wishlistContentDetailsList);
  const wishlistedProducts = computed(() => store.state.wishlistProducts);
  const isSelectWishlistModalOpen = computed(() => store.state.isWishlistModalOpen);
  const isCreatingNewList = computed(() => store.state.showCreateNewListScreen);

  const isDesktopScreen = computed(() => {
    return [MEDIA_TYPES.TABLET_EXTRA_LARGE, MEDIA_TYPES.DESKTOP, MEDIA_TYPES.DESKTOP_LARGE].includes(mediaType.value);
  });

  onMounted(() => {
    store.dispatch('fetchWishlistItems');
  });

  const toggleWishlist = async (product, selectedVariantDetails, isWishlisted, showOptions, firstOptionName) => {
    try {
      if (isWishlisted) {
        await handleRemoveFromWishlist(product, selectedVariantDetails, showOptions, firstOptionName);
      } else {
        if (!showOptions && !selectedVariantDetails?.selectedVariant?.id) {
          const unselectedOptName = getUnselectedOptionName(product, selectedVariantDetails);
          showToast({
            status: 'error',
            message: `Please select ${unselectedOptName.toLowerCase()} to add item to wishlist`,
            customMessageClass: 'wishlist-error-select-option',
            enableElipsis: false,
          });
          return;
        }
        await handleAddToWishlist(product, selectedVariantDetails, showOptions);
      }
    } catch (error) {
      console.error('Error!', error);
      store.dispatch('showWishlistModal', false);
      showToast({
        status: 'error',
        message: `Error! ${error?.msg || error?.[0]?.msg}`,
        customMessageClass: 'wishlist-error-select-option',
      });
    }
  };

  const handleRemoveFromWishlist = async (product, selectedVariantDetails, showOptions, firstOptionName) => {
    const listIds = [];
    let variantId = '';

    // Handle 'showOptions' case
    if (showOptions) {
      wishlistedProducts.value.forEach((details) => {
        if (
          details.empi == product.id &&
          ((firstOptionName == 'Color' &&
            details.cprops.variantDetails[firstOptionName] == selectedVariantDetails[firstOptionName]) ||
            firstOptionName != 'Color')
        ) {
          listIds.push(details.lid);
          variantId = details.epi; // Set variant ID to the first matching product
        }
      });
    } else {
      // Handle 'showOptions' is false (multiple lists, same variant ID)
      wishlistedProducts.value.forEach((details) => {
        if (details.empi == product.id && details.epi == selectedVariantDetails?.selectedVariant?.id) {
          listIds.push(details.lid);
        }
      });
      variantId = selectedVariantDetails?.selectedVariant?.id; // Use the selected variant's ID
    }

    // Only proceed if we have valid list IDs
    if (listIds.length) {
      const response = await deleteProductFromMultipleList(listIds, product, { selectedVariant: { id: variantId } });
      if (response) {
        store.dispatch('fetchWishlistItems');
      }
    }
  };

  const handleAddToWishlist = async (product, selectedVariantDetails, showOptions) => {
    store.dispatch('setRenderOptionsInModal', showOptions);
    store.dispatch('setProductToWishlist', { product, variant: selectedVariantDetails });
    if (wishlistContentDetailsList.value.length < 1) {
      store.dispatch('toggleCreateNewListScreen', true);
    } else {
      store.dispatch('toggleCreateNewListScreen', false);
    }
    store.dispatch('showWishlistModal', true);
  };

  const showToast = ({ status, title, message, templateType, customMessageClass, enableElipsis, duration }) => {
    const toastData = {
      status,
      title,
      message,
      templateType,
      customMessageClass,
      enableElipsis,
      duration,
      showInCurrentCursorPosition: false,
    };

    window.dispatchEvent(new CustomEvent('showToast', { detail: toastData }));
  };

  const closeToast = () => {
    window.dispatchEvent(new CustomEvent('closeToast'));
  };

  const getProductListIds = (product, selectedVariantDetails) => {
    return wishlistContentDetailsList.value.flatMap((item) =>
      item.listcontents.filter((prod) => prod.epi == selectedVariantDetails?.selectedVariant?.id).map(() => item.lid)
    );
  };

  const handleMoveToAnotherList = () => {
    closeToast();
    store.dispatch('showWishlistModal', true);
  };

  const handleSignIn = () => {
    const returnToUrl = window.location.href;
    const loginUrl = `/account/login?return_to=${encodeURIComponent(returnToUrl)}`;
    // Redirect the user
    window.location.href = loginUrl;
  };

  const getUnselectedOptionName = (product, variantDetails) => {
    const { options } = product;
    let name;

    for (let option in options) {
      if (!variantDetails[options[option].name]) {
        name = options[option].name;
        break;
      }
    }

    return name;
  };

  watch(wishlistedProducts, (val) => {
    window.dispatchEvent(new CustomEvent('refreshWishlist'));
  });

  window.toggleWishlist = toggleWishlist;
</script>
